import { useEffect, useRef } from 'react'
import { createLogger } from '@/lib/logger'
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom'

import { ErrorState } from './ErrorState'

const logger = createLogger('components/shared/FeatureErrorBoundary')

/**
 * What the crash screen says. The thrown message ("[tiptap error]: The editor
 * view is not available…") means nothing to the person who hit it, so it's
 * shown only in development; it's always logged.
 */
export function describeRouteError(error: unknown, isDev = false): string {
  if (isRouteErrorResponse(error)) {
    return error.status === 404
      ? "This page doesn't exist, or it was moved."
      : 'Something went wrong loading this page. Try again in a moment.'
  }
  const fallback =
    'Something went wrong on this page. Try again, or go back to where you were.'
  return isDev && error instanceof Error && error.message
    ? `${fallback} (${error.message})`
    : fallback
}

export interface FeatureErrorBoundaryProps {
  featureName: string
}

export function FeatureErrorBoundary({
  featureName,
}: FeatureErrorBoundaryProps) {
  const error = useRouteError()
  const navigate = useNavigate()
  const hasLogged = useRef(false)
  useEffect(() => {
    if (hasLogged.current) return
    logger.error(`${featureName} route error`, error)
    hasLogged.current = true
  }, [error, featureName])

  const description = describeRouteError(error, import.meta.env.DEV)

  return (
    <ErrorState
      // Feature names are plural ("Notes", "Tickets"): "Notes is
      // unavailable" read wrong.
      title={`Couldn't open ${featureName}`}
      description={description}
      onRetry={() => navigate(0)}
      onBack={() => navigate(-1)}
      fullScreen
    />
  )
}
